import { appPageBoilerplateFetch } from '@symbo-ls/workspace-shell/functions/pageBoilerplate.js'
// avatarUrl (below, inside resolveAssignees) comes from
// @symbo-ls/workspace-shared, stubbed by frank's EXTERNAL_PACKAGES at
// serialization time — reached via el.call instead of a raw import (see
// TicketDetailModal.js for the full rationale). appPageBoilerplateFetch
// above is a DATA re-export (not a function call
// inside a handler) — el.call cannot resolve a data export (it only
// dispatches typeof === 'function' registry entries), so this is a
// DIFFERENT defect shape than the raw-import-to-el.call class this pass
// fixes: both are live cross-package raw imports that go through the
// exact same stub at frank-serialization time and silently degrade
// (a chainable no-op proxy that stringifies to ''), but the fix here needs
// either a function-wrapper added to the owning package or a restructure
// of the module-scope re-export/spread into something el.call can reach —
// flagged for a follow-up ticket rather than guessed at here.

// `githubColorToken` is NOT re-exported here. The value lives in the
// workspace-shell library, which this project does not declare, so the
// re-export bundled as a stub and published as "" (frank FA206
// stub-phantom). Views call the shell-supplied `githubColorTokenFromName`
// through the context instead (MyViewMain.js).

// Canonical tasks-route form of a (workspace-prefix-stripped) path.
// /tasks is the canonical URL segment since the tickets→tasks rename
// (2026-08-06 per Nika "instead of /tickets it will be /tasks"); the legacy
// /tickets* forms stay registered as inbound aliases, so every route matcher
// in this package normalizes through this helper and keys on the /tasks form
// only. Pure string map — the DATA plane (sdk.tickets.*, root.tickets,
// tickets state keys) is untouched by the rename.
export const canonicalTaskPath = (path) => {
  const p = String(path || '')
  if (p === '/tickets-page') return '/tasks-page'
  if (p === '/tickets') return '/tasks'
  if (p.startsWith('/tickets/')) return '/tasks/' + p.slice('/tickets/'.length)
  if (p.startsWith('/ticket/')) return '/task/' + p.slice('/ticket/'.length)
  return p
}

// Visual lookup tables shared by TicketCard + TicketDetailModal.
//
// PRIORITY_COLOR is the PLATE hue: it feeds a chip's `color-mix()` tint, and a
// raw brand hue is what a plate is for. It is NOT an ink. Every priority label
// used to ink with it, and a raw hue on its own tint fails the contrast floor
// in both themes — measured live on /tasks, `P2` read 3.41:1 on every card.
// DESIGN_LANGUAGE §8.5 sets the two recipes:
//   • a PLATED chip keeps the tint and takes `title` ink (rule 3);
//   • an UNPLATED label inks from PRIORITY_INK below (rule 2).
export const PRIORITY_COLOR = {
  urgent: 'red',
  P0: 'red',
  P1: 'orange',
  high: 'orange',
  medium: 'blue',
  P2: 'blue',
  low: 'caption',
  P3: 'caption'
}

// The ink of an UNPLATED priority label (a list row, the edit select): the
// same hues, re-toned per theme by the brand (designSystem/color.js, "Semantic
// HUE inks" — each is a [light, dark] pair). >= 4.5:1 on the bare card grounds
// of both themes; recomputed from the shipped tokens in
// packages/workspace/__tests__/ticketPriorityChipInkContrast.test.js. NOT for a plated chip: the tint
// lifts the ground, and these measured 4.24–4.36 on the lighter cards.
export const PRIORITY_INK = {
  urgent: 'dangerInk',
  P0: 'dangerInk',
  P1: 'warningInk',
  high: 'warningInk',
  medium: 'accentInk',
  P2: 'accentInk',
  low: 'caption',
  P3: 'caption'
}

// Canonical P-scale label for any priority value the data plane carries.
// The fleet writes P0–P3 directly; GitHub-synced / AI-written tickets carry
// the word scale (urgent/high/medium/low). The server enum accepts BOTH
// (server/src/core/models/Ticket.js — enum ['low','medium','high','urgent',
// 'P0'..'P3', null]), so the UI never rewrites stored data — it only maps
// the word form onto the same P-scale for display, keeping one vocabulary
// on the card and in the detail view.
export const PRIORITY_LABEL = {
  urgent: 'P0',
  P0: 'P0',
  high: 'P1',
  P1: 'P1',
  medium: 'P2',
  P2: 'P2',
  low: 'P3',
  P3: 'P3'
}

// '' for an UNSET priority — every consumer gates its chip on this being
// falsy, so an unset priority renders no chip at all (never a '—' placeholder
// on a dense board card).
export const priorityLabel = (priority) => {
  if (!priority) return ''
  return PRIORITY_LABEL[priority] || String(priority).toUpperCase()
}

// Options for the detail-view priority <select>. First entry clears the
// field (writes null through _save). Values are the canonical P-scale.
// The `text` of each option is a BUNDLE KEY, not copy — this module is plain
// data with no element to resolve through, so the option's render site
// (`TicketsDetailModal` PriorityField.Select.childExtends) calls polyglot on
// it. A legacy free-text priority pushed into the same list carries its own
// label, which polyglot returns verbatim.
export const PRIORITY_OPTIONS = [
  { value: '', text: 'tickets.priority.option.none' },
  { value: 'P0', text: 'tickets.priority.option.p0' },
  { value: 'P1', text: 'tickets.priority.option.p1' },
  { value: 'P2', text: 'tickets.priority.option.p2' },
  { value: 'P3', text: 'tickets.priority.option.p3' }
]

// Keyed BOTH ways on purpose. The Title-Case keys are the legacy display
// labels; the lower-case ones are the values actually stored on `type` (the
// server's registered discriminators). Every live reader passes the STORED
// value — `ISSUE_TYPE_COLOR[s.issueType]` on the card and in the detail
// sidebar — so with Title-Case keys alone the lookup always missed and every
// type chip fell back to its neutral ink.
export const ISSUE_TYPE_COLOR = {
  Bug: 'red',
  Feature: 'green',
  Task: 'blue',
  Enhancement: 'green',
  'User Story': 'purple',
  Epic: 'orange',
  bug: 'red',
  task: 'blue',
  spike: 'blue',
  decision: 'green',
  user_story: 'purple',
  epic: 'orange'
}

// The INK of each type's label: the brand's semantic hue pair for that type's
// hue (designSystem/color.js "Semantic HUE inks" — deepened for light,
// brightened for dark). ISSUE_TYPE_COLOR's raw hues stay the chip's PLATE (a
// 14% wash, decoration); as ink they fail light: the saved-ticket TYPE chip
// ("spike", raw `blue` #0085FF) measured 3.47:1 on the light panel (PROD,
// 2026-09-24). Purple has no brand ink; `magentaInk` is its nearest hue. The
// ratios on the modal's grounds are recomputed from the brand tokens in
// packages/workspace/__tests__/ticketPriorityChipInkContrast.test.js. Keyed
// exactly like ISSUE_TYPE_COLOR.
export const ISSUE_TYPE_INK = {
  Bug: 'dangerInk',
  Feature: 'successInk',
  Task: 'accentInk',
  Enhancement: 'successInk',
  'User Story': 'magentaInk',
  Epic: 'warningInk',
  bug: 'dangerInk',
  task: 'accentInk',
  spike: 'accentInk',
  decision: 'successInk',
  user_story: 'magentaInk',
  epic: 'warningInk'
}

export const STATE_COLOR = {
  OPEN: 'green',
  CLOSED: 'caption',
  MERGED: 'purple'
}

// Color tokens for internal ticket workflow states.
//
// Mapped 1:1 to the three-branch release pipeline (main → release → prod):
//   ready_to_review — PR open, code review pending (purple)
//   ready_to_test   — merged to main, QA on main→release preview (magenta)
//   done            — on release branch / live on staging (green)
//   released        — gh release create fired / live on prod (blue, terminal)
export const TICKET_STATE_COLOR = {
  not_started: 'caption',
  in_progress: 'yellow',
  blocked: 'red',
  awaiting_approval: 'orange',
  ready_to_review: 'purple',
  ready_to_test: 'magenta',
  done: 'green',
  released: 'blue',
  cancelled: 'caption',
  // Legacy alias — historical rows still carry 'CLOSED' until they're
  // re-saved. Resolves to the same cancelled palette.
  CLOSED: 'caption',
  closed: 'caption'
}

// Human-readable labels for all canonical ticket states.
// Falls back to title-cased raw value so new states degrade gracefully.
const STATE_LABEL = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  awaiting_approval: 'Awaiting Approval',
  ready_to_review: 'Ready to Review',
  ready_to_test: 'Ready to Test',
  done: 'Done',
  released: 'Released',
  cancelled: 'Cancelled',
  CLOSED: 'Cancelled',
  closed: 'Cancelled'
}

// The same table as bundle KEYS. `formatStateLabel` answers English and stays
// for callers that have no element (logs, exports). Every label a person reads
// goes through `stateLabelText(el, state)`: the state pill on a card, in the
// detail modal and on the General view read `In Progress` on a Georgian board
// (live walk 2026-09-21).
const STATE_LABEL_KEY = {
  not_started: 'tickets.state.notStarted',
  in_progress: 'tickets.state.inProgress',
  blocked: 'tickets.state.blocked',
  awaiting_approval: 'tickets.state.awaitingApproval',
  ready_to_review: 'tickets.state.readyToReview',
  ready_to_test: 'tickets.state.readyToTest',
  done: 'tickets.state.done',
  released: 'tickets.state.released',
  cancelled: 'tickets.state.cancelled',
  CLOSED: 'tickets.state.cancelled',
  closed: 'tickets.state.cancelled'
}

// A state nobody listed (a new server state) has no key: it degrades to the
// title-cased raw value, exactly as `formatStateLabel` always did.
export const stateLabelText = (el, state) => {
  const key = STATE_LABEL_KEY[state]
  return key && el && typeof el.call === 'function'
    ? el.call('polyglot', key)
    : formatStateLabel(state)
}

export const formatStateLabel = (state) => {
  if (!state) return ''
  if (STATE_LABEL[state]) return STATE_LABEL[state]
  // Graceful fallback: replace underscores + title-case each word
  return state.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// A board column's name as the person reads it. Every column is server data
// (`tickets.columns`), and a person may rename any column. The client keeps no
// copy of the server's seed labels: the server's columns read marks a column
// whose key and label still equal its seed row with `labelIsSeed: true`
// (computed on the read; no migration, no write). A Georgian board read
// "Backlog", "In Progress", … on every column (BESSI dev default workspace,
// 2026-10-31).
//
// A column marked `labelIsSeed: true` reads the tickets key for its key in the
// active language (docs/I18N-GLOSSARY.md, "Work states and priority"; `review`
// reads its own `tickets.kanban.column.review`, row "In review"). Every other
// column shows its stored label exactly: a renamed one (`labelIsSeed: false`),
// a column whose key has no tickets key, and every column from a server that
// does not send the field. The platform's source language (polyglot
// `defaultLang`, the language the seed rows are written in) shows the stored
// label too, and so does a language whose bundle does not carry the key (yet).
//
// Called as `el.call('columnLabelText', column)`: `this` is the element.
export function columnLabelText(column) {
  const el = this
  const col = column || {}
  const own = String(col.label || col.name || '')
  if (col.labelIsSeed !== true || !el || typeof el.call !== 'function') return own
  // A seeded column's key -> the tickets key that names it.
  const keys = {
    backlog: 'tickets.detail.status.backlog',
    todo: 'tickets.detail.status.todo',
    in_progress: 'tickets.state.inProgress',
    blocked: 'tickets.state.blocked',
    ready_to_test: 'tickets.state.readyToTest',
    review: 'tickets.kanban.column.review',
    done: 'tickets.detail.status.done'
  }
  const key = Object.prototype.hasOwnProperty.call(keys, col.key) ? keys[col.key] : ''
  const source = el.context && el.context.polyglot && el.context.polyglot.defaultLang
  if (!key || typeof source !== 'string' || !source) return own
  const lang = el.call('getActiveLang')
  if (!lang || lang === source) return own
  const text = el.call('polyglot', key)
  // Without the key in the active bundle polyglot answers the key itself or
  // the source language's text: the stored label shows then.
  if (typeof text !== 'string' || !text || text === key) return own
  return text === el.call('polyglot', key, source) ? own : text
}

// The states that end a ticket's pipeline (the three-branch model):
//   done       — merged to the release branch (live on staging)
//   released   — gh release create fired (live on prod)
//   cancelled  — won't-fix / archive
// The detail modal's dependency rows strike such a ticket through and label it
// "Resolved". They reach this through el.call: the modal mounts at the shell
// root before any tickets route folds the context, and this file rides the
// eager slice (functions/eagerSlice.js).
export function isTerminalTicketState(state) {
  return state === 'done' || state === 'released' || state === 'cancelled'
}

// A ticket ROW counts as done in the epic / story progress roll-ups when its
// state is terminal or the row is struck through (metadata.struck).
// fetchEpicsTree, fetchStoriesTree and fetchCanvasTriage count by it through
// el.call.
//
// One owner for both tests, on purpose. They used to be three module-scope
// `_isDone` helpers: this row form in fetchEpicsTree.js and
// fetchCanvasTriage.js, a state-string form in TicketDetailModal.js. frank
// emits ONE flat globalScope per library, so one name could carry only one of
// them, and frank refused the tickets library publish
// (module-scope-name-conflict on `_isDone`).
export function isTicketRowDone(row) {
  return isTerminalTicketState(row.state) || !!row.metadata?.struck
}

// Resolve a ticket's assignees into a list ready for avatar rendering.
// Prefers people we know about (so the popover gets the real name),
// falls back to the raw GitHub login + GitHub avatar URL when the user
// only exists upstream. Used by TicketCard + TicketDetailModal.
// Takes `el` first (not just ticket/people) so it can reach avatarUrl via
// el.call instead of a raw cross-package import.
export const resolveAssignees = (el, ticket, people) => {
  const out = []
  const seen = new Set()
  // Build a one-shot map for the call so the two passes below don't
  // re-walk `people` per assignee. WeakMap caching only kicks in when
  // the caller passes `root.people` directly — this local map handles
  // the smaller per-call list either way.
  const list = people || []
  const byEmail = new Map()
  const byGithub = new Map()
  for (const p of list) {
    if (p.email) byEmail.set(p.email, p)
    if (p.github) byGithub.set(p.github, p)
  }
  if (ticket?.assigneeEmail) {
    const p = byEmail.get(ticket.assigneeEmail)
    out.push({
      key: ticket.assigneeEmail,
      name: p?.name || ticket.assigneeEmail,
      avatar: p?.avatar || el.call('avatarUrl', p?.name || ticket.assigneeEmail),
      login: p?.github
    })
    seen.add(ticket.assigneeEmail)
    if (p?.github) seen.add(p.github)
  }
  for (const login of ticket?.assigneeLogins || []) {
    if (!login || seen.has(login)) continue
    const p = byGithub.get(login)
    if (p?.email && seen.has(p.email)) continue
    out.push({
      key: login,
      name: p?.name || login,
      avatar: `https://github.com/${login}.png?size=64`,
      login
    })
    seen.add(login)
    if (p?.email) seen.add(p.email)
  }
  return out
}

// Resolve a single person identifier (email OR a bare GitHub login — legacy
// GitHub-synced tickets store `reporterEmail` as the raw login, no `@`, e.g.
// "nikoloza") into a display name + avatar. Single source of truth for any
// ONE-person field (Reporter today); the multi-person case is
// resolveAssignees above. Without this, a GitHub-login reporter never
// matches `people` (keyed by real email) and falls through to showing the
// raw lowercase login with no avatar (TASKS-MICRO-1d).
export const resolvePersonDisplay = (identifier, people) => {
  if (!identifier) return null
  const list = people || []
  const byEmail = list.find((p) => p && p.email === identifier)
  if (byEmail) {
    return { name: byEmail.name || identifier, avatar: byEmail.avatar || '', email: byEmail.email }
  }
  const byGithub = list.find((p) => p && p.github === identifier)
  if (byGithub) {
    return {
      name: byGithub.name || identifier,
      avatar: byGithub.avatar || `https://github.com/${identifier}.png?size=64`,
      email: byGithub.email || null
    }
  }
  // No match anywhere — a bare login (no '@') is still a GitHub identity,
  // just for someone not in `people` yet. A real email with no match falls
  // back to initials via avatarUrl at the call site.
  const isGithubLogin = !identifier.includes('@')
  return {
    name: identifier,
    avatar: isGithubLogin ? `https://github.com/${identifier}.png?size=64` : '',
    email: isGithubLogin ? null : identifier
  }
}

// Look up a column option id by display label. Avoids hardcoding the
// GitHub Status field option ids (`'4d6ab783'` etc) which can change
// when the upstream project re-seeds.
export const columnKeyByLabel = (root, label) => {
  const c = (root?.columns || []).find((c) => c.label === label)
  return c?.key || null
}

// `ticket_columns` fetch — tickets-specific. Stacked on top of the
// cross-app `appPageBoilerplateFetch` from shared-gov so every tickets
// page gets `app_settings`, `people`, AND `ticket_columns` in one
// spread.
//
// Mongo migration: `tickets.columns` is the canonical entity now
// (EntityDispatcher.js, SDK commit 293ae0d). The old
// `workspaceProject.ticketColumns` route + WorkspaceProjectService
// back-compat getter were both dropped — anything still pointing at
// them throws `Unknown entity` and silently empties the board (and the
// stale deployed bundle continues firing `/sb/rest/v1/ticket_columns`
// PostgREST hits until it's rebuilt).
export const ticketColumnsFetch = {
  method: 'select',
  from: 'tickets.columns',
  as: '~/columns',
  cache: '5m',
  // WORKSPACE-TICKETS-COLUMNS-TRANSFORM-NONARRAY (/logs 85041/85042).
  // Defensive: when the SDK returns a truthy non-array (error envelope,
  // paginated wrapper) `rows || []` keeps the non-array and `.map`
  // throws at preboot. Array.isArray guard is the only safe coercion.
  transform: (rows) => ({
    columns: Array.isArray(rows) ? rows.map(rowToColumn) : []
  })
}

// Tickets-flavoured page boilerplate fetch — appPageBoilerplateFetch
// (shared with chat + calendar) plus the ticket_columns table that
// every tickets view needs.
export const pageBoilerplateFetch = [...appPageBoilerplateFetch, ticketColumnsFetch]

const _foldFieldValues = (fvs) => {
  const out = {}
  for (const v of fvs || []) {
    const name = v.project_fields?.name
    if (!name) continue
    let value = null
    if (v.option_name != null) value = v.option_name
    else if (v.number_value != null) value = v.number_value
    else if (v.date_value != null) value = v.date_value
    else if (v.text_value != null) value = v.text_value
    if (value != null) out[name] = value
  }
  return out
}

export const rowToTicket = (r) => {
  const customFields = _foldFieldValues(r.ticket_field_values)
  return {
    id: r.id,
    title: r.title,
    body: r.body,
    columnKey: r.column_key,
    position: r.position,
    assigneeEmail: r.assignee_email,
    assigneeLogins: r.assignee_logins || [],
    reporterEmail: r.reporter_email,
    labels: r.labels || [],
    // Priority comes from the GitHub Project's custom field — fall back
    // to the column on the tickets table if a future migration adds it.
    priority: customFields.Priority || r.priority || null,
    dueDate: customFields['Due Date'] || r.due_date,
    githubUrl: r.github_issue_url,
    githubNodeId: r.github_node_id,
    // Mirrored Projects v2 metadata (see migrations 0014 / 0016).
    number: r.number,
    contentType: r.content_type, // 'Issue' | 'PullRequest' | 'DraftIssue'
    state: r.state, // 'OPEN' | 'CLOSED' | 'MERGED'
    repository: r.repository, // 'symbo-ls/smbls'
    milestone: r.milestone,
    iterationId: r.iteration_id,
    iterationTitle: r.iteration_title || customFields.Iteration,
    iterationStart: r.iteration_start,
    iterationEnd: r.iteration_end,
    trackedByCount: r.tracked_by_count || 0,
    tracksCount: r.tracks_count || 0,
    // Issues 2.0 — GitHub native issue type + the project-level Epic field.
    issueType: r.issue_type,
    issueTypeColor: r.issue_type_color,
    epicOptionId: r.epic_option_id,
    epicName: r.epic_name || customFields.Epic,
    parentIssueUrl: r.parent_issue_url,
    subIssuesTotal: r.sub_issues_total || 0,
    subIssuesCompleted: r.sub_issues_completed || 0,
    // Common Projects v2 custom fields, surfaced as first-class props
    // for the card UI. Anything not in this list is still available via
    // `customFields[name]`.
    severity: customFields.Severity || null,
    storyPoints: customFields['Story Points'] || null,
    size: customFields.Size || null,
    estimate: customFields.Estimate || null,
    environment: customFields.Environment || null,
    reviewStatus: customFields['Review Status'] || null,
    customFields,
    createdAt: r.created_at,
    updatedAt: r.updated_at
  }
}

export const rowToColumn = (r) => ({
  id: r.id,
  key: r.key,
  label: r.label,
  position: r.position,
  color: r.color, // GitHub color name (GRAY/GREEN/etc.)
  description: r.description,
  optionId: r.option_id,
  // The server's mark for a column whose key and label still equal its seed
  // row (columnLabelText reads it). A server that does not send it adds nothing.
  ...(typeof r.labelIsSeed === 'boolean' ? { labelIsSeed: r.labelIsSeed } : {})
})

// Filter + sort tickets that belong to a given column. Honours the active
// filter slots on root state so the board re-renders reactively when the
// user toggles a chip.
//
// Memoised: a single pass over `root.tickets` builds the entire
// `columnKey → tickets[]` index, cached on a WeakMap keyed off the
// `root.tickets` array identity. Subsequent calls (12 columns × 2 cell
// renderers per render) hit the cache. The cache is invalidated when
// `root.tickets` is replaced wholesale (the smbls update pattern) or
// when the filter signature changes — both cheap O(1) checks.
const _columnIndexCache = new WeakMap()
const _EMPTY_LIST = Object.freeze([])

// Person-filter slots (filterAssignee / filterIssuer) accept an ARRAY of
// emails (multi-select sidebar pickers), a single email string (legacy),
// or a category string ('me'/'team'/'agents' — assignee only). Normalize
// explicit person picks to an array; categories/null return [].
export const personFilterEmails = (v) => {
  if (!v || v === 'all') return _EMPTY_LIST
  if (Array.isArray(v)) return v
  if (v === 'me' || v === 'team' || v === 'agents') return _EMPTY_LIST
  return [v]
}

// ONE source of truth for "which epics exist" — unions epic-TYPE ticket
// titles (root.epicsTree, lazy-loaded by loadEpicsTree) with the distinct
// non-empty free-text `epicName` values already used on loaded tickets
// (root.tickets). Both the sidebar EPIC filter (ticketsEpicPickItems) and
// the ticket modal's Epic select read THIS so they can never disagree about
// which epics are pickable — a workspace can have tickets tagged with an
// epic name before anyone ever formalizes it as an epic-type ticket (the
// natali workspace's warehouse/commerce/foundation/… set, 2026-08-02).
// Dedupes case-insensitively (first-seen casing wins — epicsTree titles are
// read first so a formalized epic's casing takes priority over stray
// free-text variants); sorted alphabetically for a predictable picker order.
export const ticketsEpicNames = (root) => {
  const seen = new Map() // lowercase key -> display casing
  const add = (name) => {
    const trimmed = (name || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (!seen.has(key)) seen.set(key, trimmed)
  }
  for (const ep of root?.epicsTree || []) add(ep?.title)
  for (const t of root?.tickets || []) add(t?.epicName)
  return Array.from(seen.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  )
}

const _filterSignature = (root) =>
  `${[].concat(root.filterAssignee || []).join(',')}|${[]
    .concat(root.filterIssuer || [])
    .join(',')}|${root.filterLabel || ''}|${root.filterEpic || ''}|${root.filterIssueType || ''}|${(
    root.searchQuery || ''
  )
    .toLowerCase()
    .trim()}|${root.currentUserEmail || ''}|${root.filterProjectId || ''}`

// The two PLANNING discriminators. They have their own surfaces
// (/tickets/epics, /tickets/stories) and never appear as work-board cards
// (2026-06-10 per Nika "stories and epics should not be listed as
// tickets/tasks"). ONE list for all three readers: `ticketBelongsOnBoard`
// below, the "where did my card go" notice in TicketDetailModal, and the
// server-side query default in ticketLoaders.js's `_buildMongoFilter`.
export const BOARD_PLANNING_TYPES = ['epic', 'user_story']

// Single-ticket predicate: does this ticket belong on THIS board right now?
// Extracted from _buildColumnIndex's per-row filter (2026-08-02 per Nika
// "make sure tickets page is fully chained on socket realtime") so a
// realtime INSERT (pages/board.js) can ask the EXACT same question for one
// incoming row instead of a second, drifting implementation. Three checks,
// structural first:
//   1. Workspace — only enforced when BOTH sides are known (defensive; a
//      normal scoped fetch never puts another workspace's row in
//      root.tickets, but the ticket SSE stream does NOT actually enforce
//      workspace scope server-side despite requiring a workspaceId param —
//      TicketStreamService._matchFilter, server/src/domains/tickets/
//      services/TicketStreamService.js:92-104, checks ownerOrganization/
//      project/assigneeEmail/state/epic but never `filter.workspace` — so a
//      realtime INSERT needs this check even though a fetch never would).
//   2. Type — epics/user stories are PLANNING items, excluded from the work
//      board by default (mirrors _buildMongoFilter's server-side `$nin`
//      default exactly; an explicit TYPE filter still wins, same as server).
//   3. The active person/label/epic/search filters — unchanged from before
//      this extraction.
export const ticketBelongsOnBoard = (root, t) => {
  if (!root || !t) return false
  if (root.workspace?.id && t.workspaceId && String(t.workspaceId) !== String(root.workspace.id)) {
    return false
  }
  // Mongo rows carry the kind on `type` (the legacy Supabase shape's
  // `issueType` is hardcoded null in _mongoToTicket) — checking only
  // t.issueType dropped EVERY server-filtered row client-side, blanking
  // the board whenever a type filter was active.
  const issueType = root.filterIssueType
  const kind = t.issueType || t.type
  if (issueType) {
    if (kind !== issueType) return false
  } else if (BOARD_PLANNING_TYPES.includes(kind)) {
    return false
  }
  const me = root.currentUserEmail
  const assignee = root.filterAssignee
  const issuer = root.filterIssuer
  const assigneeEmails = personFilterEmails(assignee)
  const issuerEmails = personFilterEmails(issuer)
  const label = root.filterLabel
  const epic = root.filterEpic
  const q = (root.searchQuery || '').trim().toLowerCase()
  if (assigneeEmails.length) {
    if (!assigneeEmails.includes(t.assigneeEmail)) return false
  } else if (assignee === 'me' && t.assigneeEmail !== me) return false
  // Issuer (reporter) — one or more people picked in the sidebar.
  if (issuerEmails.length && !issuerEmails.includes(t.reporterEmail)) return false
  if (label && !(t.labels || []).includes(label)) return false
  // Epic filter is NAME-based (Mongo `epic` column → epicName) — the old
  // epicOptionId GUID is never set on Mongo rows.
  if (epic && t.epicName !== epic) return false
  // The project scope (/tasks?project=<id>): a realtime row of another
  // project stays off the board. A row that carries no project field at all
  // (an older normalizer) is the server's read and already scoped.
  const project = root.filterProjectId
  if (project && t.project !== undefined && String(t.project || '') !== String(project)) {
    return false
  }
  if (
    q &&
    !((t.title || '').toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q))
  ) {
    return false
  }
  return true
}

// Why is this ticket not on the board? Returns null when it IS on the board,
// 'planning-type' when its own type excludes it, 'filters' when the active
// filters do. TicketDetailModal asks after every save, so a card that leaves
// the board says so instead of vanishing (TICKETS | Tickets of type Feature
// or User Story disappear after being created, P0 2026-09-03).
//
// The server NORMALIZES on write — `feature` and `user-story` both store as
// `user_story` (server Ticket model, ticketTypeDiscriminatorEnum.test.js) —
// so a ticket the user labelled "Feature" lands on the planning list too.
export const ticketHiddenFromBoardReason = (root, t) => {
  if (!root || !t) return null
  if (ticketBelongsOnBoard(root, t)) return null
  const kind = t.issueType || t.type
  if (!root.filterIssueType && BOARD_PLANNING_TYPES.includes(kind)) return 'planning-type'
  return 'filters'
}

const _buildColumnIndex = (root) => {
  const byCol = new Map()
  for (const t of root.tickets || []) {
    if (!ticketBelongsOnBoard(root, t)) continue
    let arr = byCol.get(t.columnKey)
    if (!arr) {
      arr = []
      byCol.set(t.columnKey, arr)
    }
    arr.push(t)
  }
  for (const arr of byCol.values()) {
    arr.sort((a, b) => (a.position || 0) - (b.position || 0))
  }
  return byCol
}

export function getTicketsForColumn(root, columnKey) {
  if (!root || !root.tickets) return _EMPTY_LIST
  let entry = _columnIndexCache.get(root.tickets)
  const sig = _filterSignature(root)
  if (!entry || entry.sig !== sig) {
    entry = { sig, byCol: _buildColumnIndex(root) }
    _columnIndexCache.set(root.tickets, entry)
  }
  return entry.byCol.get(columnKey) || _EMPTY_LIST
}

// Count of a column's rows currently in the client store — the SINGLE
// source of truth for "how much of this column is loaded". The old
// loadedByColumn counter drifted: creates (modal / meet suggestions),
// drag-moves, freeform placement and deletes all mutate root.tickets
// without maintaining it, while the board's realtime/poll counts refresh
// DOES advance totalByColumn — so a column visibly holding both of its
// rows still showed "More · 1 / 2", and clicking it re-fetched a row it
// already had, duplicating cards (2026-07-29 per Nika). Raw columnKey
// match, no client-filter/freeform exclusion: server-side filters already
// shape root.tickets, and canvas-placed cards carry columnKey 'draft'.
export function countTicketsInColumn(root, columnKey) {
  const list = root?.tickets
  if (!Array.isArray(list)) return 0
  let n = 0
  for (const t of list) if (t && t.columnKey === columnKey) n++
  return n
}

// Correlate a realtime INSERT echo back to the OPTIMISTIC row this client
// created a moment ago (the `_pendingTicketCreates` breadcrumb written by
// TicketDetailModal's _save). The echo carries the real Mongo id; the local
// row still carries a client temp id, so an id lookup can never match them —
// without this the echo is appended as a SECOND card for one ticket.
//
// Title + a time window ONLY. It used to also require
// `p.columnKey === row.columnKey`, which made the match fail exactly when it
// mattered most: a create in Todo was stored by the server in Backlog
// (PLATFORM-TICKET-CREATE-COLUMN-DUPLICATE-1), so the echo's column differed
// from the optimistic one and the card was appended — one record, two cards,
// one in Backlog and one in Todo. The server is authoritative about the
// column, so the column must never be part of the IDENTITY test: whenever
// the server normalizes a field, correlation has to survive it, or the UI
// duplicates the row instead of adopting the correction.
//
// Pure + exported so the duplicate case is unit-testable without a board.
export const PENDING_CREATE_WINDOW_MS = 20000

export function matchPendingCreate(pendingCreates, row, now = Date.now()) {
  if (!Array.isArray(pendingCreates) || !pendingCreates.length || !row) return null
  const title = row.title
  if (!title) return null
  // Newest first — two quick creates with the SAME title must consume their
  // own breadcrumbs, never the same one twice.
  const candidates = pendingCreates
    .filter((p) => p && p.title === title && now - (p.ts || 0) < PENDING_CREATE_WINDOW_MS)
    .sort((a, b) => (b.ts || 0) - (a.ts || 0))
  return candidates[0] || null
}

// Compute a fractional `position` value for a ticket dropped between two
// neighbours. Returns the midpoint so we never have to renumber the column.
export function positionBetween(prev, next) {
  if (prev == null && next == null) return 1000
  if (prev == null) return next - 1000
  if (next == null) return prev + 1000
  return (prev + next) / 2
}

// Drag a column HEADER to reorder columns (per Nika 2026-06-10 "I should be
// able to drag columns"). Same zoom-aware pointer-drag pattern as the cards:
// a scaled ghost of the header follows the cursor; dropping over another
// column inserts before/after it (cursor side). New order is written
// optimistically to root.columns (position = index*10) and each changed
// column persists via PUT /tickets/columns/:key.
export const startColumnDrag = function (e, colKey) {
  const headerEl = this && this.node
  const root = this?.state?.root
  if (!headerEl || !root || !colKey) return
  const colEl = headerEl.closest('[data-col-key]')
  if (!colEl) return
  e.preventDefault()
  // Track the initiating pointer so a second finger landing mid-drag can't
  // hijack the reorder (mouse always reports pointerId 1). POINTER events (not
  // mouse) let a finger reorder columns on iPad too.
  const pointerId = e.pointerId
  const self = this
  const startX = e.clientX
  const startY = e.clientY
  const rect = headerEl.getBoundingClientRect()
  const offX = startX - rect.left
  const offY = startY - rect.top
  const scale = (typeof window !== 'undefined' && window.__ticketsPzScale) || 1
  let ghost = null
  let indicator = null
  let dragging = false
  // Insertion indicator — a vertical bar at the edge where the column will
  // land (before/after the hovered column by cursor side), so the drop
  // destination is visible while dragging.
  const _placeIndicator = (ev) => {
    const under = document.elementFromPoint(ev.clientX, ev.clientY)
    const target = under && under.closest && under.closest('[data-col-key]')
    const targetKey = target && target.getAttribute('data-col-key')
    if (!target || targetKey === colKey) {
      if (indicator) indicator.style.display = 'none'
      return
    }
    if (!indicator) {
      indicator = document.createElement('div')
      const st = indicator.style
      st.position = 'fixed'
      st.width = '4px'
      st.borderRadius = '2px'
      st.background = 'var(--color-blue, #3a7bfd)'
      st.boxShadow = '0 0 12px var(--color-blue, #3a7bfd)'
      st.pointerEvents = 'none'
      st.zIndex = '99998'
      document.body.appendChild(indicator)
    }
    const r = target.getBoundingClientRect()
    const after = ev.clientX > r.left + r.width / 2
    indicator.style.left = Math.round((after ? r.right : r.left) - 2) + 'px'
    indicator.style.top = Math.round(r.top) + 'px'
    indicator.style.height = Math.round(r.height) + 'px'
    indicator.style.display = 'block'
  }
  const onMove = (ev) => {
    if (ev.pointerId !== pointerId) return
    if (!dragging) {
      if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) < 6) return
      dragging = true
      ghost = headerEl.cloneNode(true)
      const gs = ghost.style
      gs.position = 'fixed'
      gs.margin = '0'
      gs.left = '0'
      gs.top = '0'
      gs.width = headerEl.offsetWidth + 'px'
      gs.pointerEvents = 'none'
      gs.opacity = '0.9'
      gs.zIndex = '99999'
      gs.transformOrigin = 'top left'
      gs.background = 'rgba(20,20,22,0.95)'
      gs.borderRadius = '12px'
      document.body.appendChild(ghost)
      colEl.style.opacity = '0.4'
    }
    if (ghost) {
      ghost.style.transform =
        'translate(' +
        (ev.clientX - offX) +
        'px,' +
        (ev.clientY - offY) +
        'px) scale(' +
        scale +
        ')'
      _placeIndicator(ev)
    }
  }
  const onUp = (ev) => {
    if (ev && ev.pointerId !== pointerId) return
    document.removeEventListener('pointermove', onMove, true)
    document.removeEventListener('pointerup', onUp, true)
    document.removeEventListener('pointercancel', onUp, true)
    if (ghost) {
      ghost.remove()
      ghost = null
    }
    if (indicator) {
      indicator.remove()
      indicator = null
    }
    colEl.style.opacity = ''
    if (!dragging) return
    const under = document.elementFromPoint(ev.clientX, ev.clientY)
    const targetCol = under && under.closest && under.closest('[data-col-key]')
    const targetKey = targetCol && targetCol.getAttribute('data-col-key')
    if (!targetKey || targetKey === colKey) return
    // Current visual order — live columns by position, or the fallback set.
    const live = (root.columns || []).filter((c) => c.key !== 'unsynced')
    const ordered = live.length
      ? live.slice().sort((a, b) => (a.position || 0) - (b.position || 0))
      : Object.keys(root.totalByColumn || {})
          .filter((k) => k !== 'unsynced')
          .map((key, i) => ({ key, label: key, position: i }))
    const keys = ordered.map((c) => c.key)
    const fromIdx = keys.indexOf(colKey)
    const targetIdx = keys.indexOf(targetKey)
    if (fromIdx < 0 || targetIdx < 0) return
    const tRect = targetCol.getBoundingClientRect()
    const after = ev.clientX > tRect.left + tRect.width / 2
    let toIdx = targetIdx + (after ? 1 : 0)
    if (fromIdx < toIdx) toIdx--
    if (toIdx === fromIdx) return
    const nextKeys = keys.slice()
    nextKeys.splice(fromIdx, 1)
    nextKeys.splice(toIdx, 0, colKey)
    const byKey = {}
    ordered.forEach((c) => {
      byKey[c.key] = c
    })
    const nextCols = nextKeys.map((k, i) => ({ ...byKey[k], position: i * 10 }))
    root.update({ columns: nextCols })
    ;(async () => {
      try {
        const svc = await self.sdk.getService('tickets')
        for (const c of nextCols) {
          const prev = byKey[c.key]
          if ((prev.position || 0) !== c.position) {
            await svc.columns.update(c.key, { position: c.position })
          }
        }
      } catch (err) {
        console.warn('[startColumnDrag] persist failed:', err?.message || err)
      }
    })()
  }
  document.addEventListener('pointermove', onMove, true)
  document.addEventListener('pointerup', onUp, true)
  document.addEventListener('pointercancel', onUp, true)
}

// Zoom-aware custom drag for ticket cards (per Nika #3). Native HTML5 drag shows
// the card at its UNSCALED size on a zoomed canvas; this replaces it with a
// pointer drag whose floating ghost is scaled by the live panzoom scale
// (window.__ticketsPzScale) so it matches the on-canvas card. Drop target is
// resolved via elementFromPoint -> [data-col-key], then optimistic update +
// moveTicket. `this` is the card element (call via el.call('startTicketDrag', e, id)).
export const startTicketDrag = function (e, ticketId) {
  if (!ticketId) return
  const cardEl = this && this.node
  const root = this && this.state && this.state.root
  if (!cardEl || !root) return
  e.preventDefault()
  // Track the initiating pointer so a second finger landing mid-drag can't
  // hijack the drag (mouse always reports pointerId 1). POINTER events (not
  // mouse) let a finger drag a card on iPad too.
  const pointerId = e.pointerId
  const self = this
  const startX = e.clientX
  const startY = e.clientY
  const rect = cardEl.getBoundingClientRect()
  const offX = startX - rect.left
  const offY = startY - rect.top
  const scale = (typeof window !== 'undefined' && window.__ticketsPzScale) || 1
  let ghost = null
  let dragging = false
  // Drop-target highlight — a clean accent BORDER in the hovered column's OWN
  // status accent (so each lane lights up its colour). Just a border, no shadow
  // (per Nika). `outline` (not `border`) so there's zero layout shift, with a
  // negative offset so it hugs the column edge like a border. Imperative (like
  // the ghost above) so a fast drag never churns root state / re-renders the
  // whole board. Cleared on column change and on drop.
  let hoverCol = null
  const setHoverCol = (colEl) => {
    if (colEl === hoverCol) return
    if (hoverCol) {
      hoverCol.style.outline = ''
      hoverCol.style.outlineOffset = ''
    }
    hoverCol = colEl
    if (colEl) {
      const token = COLUMN_ACCENT[colEl.getAttribute('data-col-key')] || 'blue'
      colEl.style.outline = '2px solid var(--color-' + token + ')'
      colEl.style.outlineOffset = '-2px'
    }
  }
  // Attach-target highlight — same outline grammar as the column highlight,
  // on the hovered `data-ref-drop` surface (chat composer / AI prompt), so
  // the gesture reads as "this will ATTACH, not move" before release.
  let hoverAttach = null
  const setHoverAttach = (elx) => {
    if (elx === hoverAttach) return
    if (hoverAttach) {
      hoverAttach.style.outline = ''
      hoverAttach.style.outlineOffset = ''
    }
    hoverAttach = elx
    if (elx) {
      elx.style.outline = '2px solid var(--color-blue)'
      elx.style.outlineOffset = '-2px'
    }
  }
  const onMove = (ev) => {
    if (ev.pointerId !== pointerId) return
    if (!dragging) {
      if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) < 5) return
      dragging = true
      ghost = cardEl.cloneNode(true)
      const gs = ghost.style
      gs.position = 'fixed'
      gs.margin = '0'
      gs.left = '0'
      gs.top = '0'
      gs.width = cardEl.offsetWidth + 'px'
      gs.pointerEvents = 'none'
      gs.opacity = '0.9'
      gs.zIndex = '99999'
      gs.transformOrigin = 'top left'
      document.body.appendChild(ghost)
      cardEl.style.opacity = '0.35'
    }
    if (ghost) {
      ghost.style.transform =
        'translate(' +
        (ev.clientX - offX) +
        'px,' +
        (ev.clientY - offY) +
        'px) scale(' +
        scale +
        ')'
    }
    // Highlight the surface under the cursor (the ghost has
    // pointerEvents:none, so elementFromPoint sees beneath it): an attach
    // target wins over a column — releasing there never moves the card.
    const overEl = document.elementFromPoint(ev.clientX, ev.clientY)
    const attachOver = (overEl && overEl.closest && overEl.closest('[data-ref-drop]')) || null
    setHoverAttach(attachOver)
    setHoverCol(
      attachOver ? null : (overEl && overEl.closest && overEl.closest('[data-col-key]')) || null
    )
  }
  const onUp = (ev) => {
    if (ev && ev.pointerId !== pointerId) return
    document.removeEventListener('pointermove', onMove, true)
    document.removeEventListener('pointerup', onUp, true)
    document.removeEventListener('pointercancel', onUp, true)
    if (ghost) {
      ghost.remove()
      ghost = null
    }
    setHoverCol(null)
    setHoverAttach(null)
    cardEl.style.opacity = ''
    if (!dragging) return
    // A COMPLETED pointer drag still gets a native trailing `click` on
    // whatever sits under the pointer (pointer events don't suppress it the
    // way HTML5 dnd does) — when the drop lands on/near the origin card that
    // click hit the card's onClick and OPENED the just-moved ticket
    // (2026-08-01 per Nika: "drag and drop should only be moving them
    // around — no accidental click"). Swallow exactly one click, capture
    // phase so no handler sees it; the timeout clears the trap when no
    // click follows (drop outside the window, pointercancel).
    const swallowClick = (ce) => {
      ce.stopPropagation()
      ce.preventDefault()
      document.removeEventListener('click', swallowClick, true)
    }
    document.addEventListener('click', swallowClick, true)
    setTimeout(() => document.removeEventListener('click', swallowClick, true), 400)
    const under = document.elementFromPoint(ev.clientX, ev.clientY)
    // Attach drop (2026-08-06 per Nika "task drag and drop in chat should
    // not change position of task ticket - it should get attached ... and
    // revert to its original position"): released over a declared ref-drop
    // surface (chat composer / AI prompt — `data-ref-drop`), the gesture is
    // an ATTACH, never a move. Deliver the ticket ref through the target's
    // own native onDrop (the same wire format TicketCard's native dragstart
    // stamps — shared/functions/dragRef.js) and leave the board state
    // UNTOUCHED: nothing was committed during the drag, so the card is
    // already back in its original position the moment the ghost is gone.
    const attachEl = under && under.closest && under.closest('[data-ref-drop]')
    if (attachEl) {
      const t = (root.tickets || []).find((x) => String(x.id) === String(ticketId)) || {}
      try {
        self.call(
          'dispatchRefDrop',
          attachEl,
          {
            kind: 'ticket',
            id: ticketId,
            title: t.title || '',
            href: `/tasks?ticket=${ticketId}`,
            meta: { number: t.number, state: t.state }
          },
          { clientX: ev.clientX, clientY: ev.clientY }
        )
      } catch (err) {
        console.warn('[startTicketDrag] ref attach failed', err?.message || err)
      }
      return
    }
    const colEl = under && under.closest && under.closest('[data-col-key]')
    const colKey = colEl && colEl.getAttribute('data-col-key')
    if (colKey) {
      // Drop into a column. Clears any freeform placement (the card returns
      // to in-column flow) alongside the column/position move.
      const inCol = (root.tickets || []).filter((t) => t.columnKey === colKey)
      const lastPos = inCol.length ? Math.max(...inCol.map((t) => t.position || 0)) : 0
      const nextPos = positionBetween(lastPos, null)
      const liveMap = { ...(root._ticketCanvasPos || {}) }
      delete liveMap[ticketId]
      root.update({
        tickets: (root.tickets || []).map((t) =>
          t.id === ticketId
            ? {
                ...t,
                columnKey: colKey,
                position: nextPos,
                metadata: { ...(t.metadata || {}), canvasPos: null }
              }
            : t
        ),
        _ticketCanvasPos: liveMap
      })
      try {
        // ONE request: moveTicket carries the placement clear with the
        // column move (OUTSIDE A COLUMN ⟺ DRAFT). The old second call could
        // fail alone and leave the position on the server under a real
        // column — the exact shape measured on dev.
        self.call('moveTicket', { ticketId, columnKey: colKey, position: nextPos })
      } catch (err) {
        console.warn('[startTicketDrag] moveTicket failed', err)
      }
      return
    }
    // Freeform drop (#32): mode on (the default) + released over the board
    // canvas (not a column) → place the card at the scene position under the
    // cursor. The ticket leaves its column and becomes a DRAFT (columnKey
    // 'draft') — drafts are hidden unless the sidebar Drafts filter is on, so
    // the filter auto-enables here so the just-dropped card never vanishes.
    if (root._ticketsFreeform !== false) {
      const boardEl =
        cardEl.closest('[data-tickets-board]') ||
        (under && under.closest && under.closest('[data-tickets-board]'))
      if (!boardEl) return
      const bRect = boardEl.getBoundingClientRect()
      const liveScale = (typeof window !== 'undefined' && window.__ticketsPzScale) || scale
      const x = Math.round((ev.clientX - offX - bRect.left) / liveScale)
      const y = Math.round((ev.clientY - offY - bRect.top) / liveScale)
      // Keep the exact in-column layout width when the card moves to the
      // absolutely-positioned freeform layer. offsetWidth is already in board
      // scene units, unlike getBoundingClientRect() which includes panzoom.
      const canvasPos = { x, y, width: cardEl.offsetWidth }
      const fromCol = ((root.tickets || []).find((t) => t.id === ticketId) || {}).columnKey
      const totals = { ...(root.totalByColumn || {}) }
      if (fromCol && fromCol !== DRAFT_COLUMN_KEY) {
        totals[fromCol] = Math.max(0, (totals[fromCol] || 1) - 1)
        totals[DRAFT_COLUMN_KEY] = (totals[DRAFT_COLUMN_KEY] || 0) + 1
      }
      root.update({
        _ticketCanvasPos: { ...(root._ticketCanvasPos || {}), [ticketId]: canvasPos },
        totalByColumn: totals,
        tickets: (root.tickets || []).map((t) =>
          t.id === ticketId
            ? {
                ...t,
                columnKey: DRAFT_COLUMN_KEY,
                column_key: DRAFT_COLUMN_KEY,
                metadata: { ...(t.metadata || {}), canvasPos }
              }
            : t
        )
      })
      try {
        if (root._showDrafts !== true) self.call('setTicketsBoardPref', { _showDrafts: true })
        if (fromCol !== DRAFT_COLUMN_KEY) {
          // ONE request: the column becomes `draft` and the position lands in
          // the same write. As two calls, the column write was refused (400
          // unknown_column — `draft` was not an accepted key) while the
          // position write succeeded: a `todo` card floating on the canvas
          // that the Drafts filter could never hide (9 such rows on dev).
          self.call('moveTicket', { ticketId, columnKey: DRAFT_COLUMN_KEY, position: 0, canvasPos })
        } else {
          // Already a draft: a pure re-position.
          self.call('setTicketCanvasPos', ticketId, canvasPos)
        }
      } catch (err) {
        console.warn('[startTicketDrag] draft placement failed', err)
      }
    }
  }
  document.addEventListener('pointermove', onMove, true)
  document.addEventListener('pointerup', onUp, true)
  document.addEventListener('pointercancel', onUp, true)
}

// Convert a column key into the brand-aligned colour token used by
// TicketCard's status accent stripe. Hoisted to module scope so the
// hot-path map literal isn't allocated on every call.
const COLUMN_ACCENT = {
  backlog: 'caption',
  todo: 'blue',
  in_progress: 'orange',
  review: 'purple',
  done: 'green'
}

export function getColumnAccent(columnKey) {
  return COLUMN_ACCENT[columnKey] || 'caption'
}

// Semantic board column keys that count as "currently being worked on"
// — used by the My View page to bucket assigned tickets into "active"
// vs "backlog" rails. These are the canonical Mongo column keys (see
// sdk.tickets.columns.list → unsynced/backlog/todo/in_progress/
// ready_to_test/review/done); the old GitHub Status field GUIDs
// ('4d6ab783' etc.) are NOT what the server persists, so matching on
// them silently bucketed every in-progress ticket as "backlog" and made
// "Start on" appear not to stick after refresh. Hoisted so the lookup
// set isn't reallocated on every filter pass.
const ACTIVE_COLUMN_KEYS = new Set(['in_progress', 'review', 'ready_to_test'])

// Canonical "In Progress" column key — what "Start on" / "Claim" move a
// ticket into so the change survives a reload (the server normalises any
// non-canonical key, e.g. the legacy '4d6ab783', back to this).
export const IN_PROGRESS_COLUMN_KEY = 'in_progress'

// Canonical "In Review" column key — "Ready to Review" moves here.
export const REVIEW_COLUMN_KEY = 'review'

// Canonical "Done" column key — used by the Epics index hero stat to
// compute the % done across the project.
export const DONE_COLUMN_KEY = 'done'

// Draft status — tickets dragged OUT of a column (freeform placement) land
// here. Not a real workflow column: a draft WITH a canvas placement always
// renders on the freeform layer regardless of the Drafts filter; a draft
// WITHOUT one (dragged out, not yet given a spot) is hidden until the
// sidebar Drafts filter (root._showDrafts) is on. See ticketPlacement below
// — this file previously said drafts were unconditionally gated by
// _showDrafts, which was the root of the "freeform tickets not showing to
// new mac" bug (2026-08-02 per Nika; every deliberately-placed card carries
// columnKey:'draft', so on a fresh device — _showDrafts defaults OFF and
// lives ONLY in localStorage, see boardPrefs.js — it vanished from BOTH the
// column list and the freeform layer with no empty-state or counter).
export const DRAFT_COLUMN_KEY = 'draft'

// Resolve a ticket's live canvas placement — the (x, y[, width]) it was
// dropped at on the freeform board, or null when it has none. Checks the
// SESSION-live map first (root._ticketCanvasPos, written by startTicketDrag
// before the persist round-trip lands) then the persisted
// ticket.metadata.canvasPos. ONE lookup — TicketsKanban's column lists,
// board.js's FreeformCards layer, and its per-card width/left/top style
// factories all read through this so they can never independently disagree
// about whether (or where) a ticket is placed.
export function ticketCanvasPos(root, ticket) {
  if (!ticket) return null
  return (root?._ticketCanvasPos || {})[ticket.id] || ticket.metadata?.canvasPos || null
}

// Where a ticket renders in freeform mode — 'freeform' (board.js's
// FreeformCards canvas layer), 'column' (its normal or draft column's
// in-flow list), or 'hidden' (a draft with NO placement, Drafts filter
// off — the one case that's still intentionally invisible: an un-triaged
// card the user dragged out but hasn't positioned or asked to see).
//
// This is the ONE place the decision is made; TicketsKanban.js and board.js
// each only ask "is this ticket's placement mine to render?", so they can
// never drift into a gap (or an overlap) between them.
//
// THE DRAFTS FILTER IS AUTHORITATIVE FOR EVERY DRAFT — placed or not
// (2026-09-02 per Nika "drafts selector should hide drafts - now it does not
// work"). The draft gate is tested FIRST, before the placement check.
// Previously the placement check came first and returned 'freeform' straight
// away, so a draft that had ever been dropped on the canvas could not be
// hidden by anything: measured live on dev.my.symbols.app with the filter
// OFF — 5 drafts, all 5 placed, all 5 still painted.
//
// This supersedes the 2026-08-02 invariant ("a placed ticket is ALWAYS
// freeform"), which was written for the "freeform tickets not showing to new
// mac" report. That report's real cause was the DEFAULT, not the gate: a
// fresh device starts with no localStorage, and `showDrafts` defaulted OFF,
// so deliberately-placed cards vanished with no empty state. The default is
// ON now (boardPrefs.js), which fixes that case at its source and leaves the
// filter free to mean what its label says.
//
// OUTSIDE A COLUMN ⟺ DRAFT (2026-09-02 per Nika "ticket is outside column -
// meaning it is draft. if its not in column - it should be draft"). A canvas
// position IS what "outside a column" means, so a placed ticket is a draft
// for the filter whatever its stored columnKey says. Measured on dev: 9 rows
// carried a persisted canvasPos with columnKey `todo`/`backlog` — they
// floated outside every column, and with the filter OFF they stayed, because
// the old gate below only consulted columnKey. The server now keeps the two
// halves consistent on every write (TicketService._reconcileCanvasPlacement)
// and a legacy row heals on its next write; this read rule makes the board
// right for the rows that have not been written yet.
export function ticketPlacement(root, ticket) {
  if (!ticket) return 'hidden'
  const placed = !!ticketCanvasPos(root, ticket)
  const draft = placed || ticket.columnKey === DRAFT_COLUMN_KEY
  if (draft && root?._showDrafts !== true) return 'hidden'
  if (placed) return 'freeform'
  return 'column'
}

// Shareable deep-link for a ticket — the CURRENT page URL + ?ticket=<ref>
// (2026-07-25 per Nika "add a copyable URL to each ticket"). Element method
// (el.call('ticketShareUrl', ref)) so the card context menu and the detail
// modal build the exact link shape the modal's URL-restore understands,
// including the /w/<org> prefix and any active filter params.
export const ticketShareUrl = function (ref) {
  if (typeof window === 'undefined' || !ref) return ''
  const url = new URL(window.location)
  url.searchParams.set('ticket', ref)
  return url.href
}

export const isActiveColumn = (columnKey) => ACTIVE_COLUMN_KEYS.has(columnKey)

// Map a tickets-table row → the StoryCard payload that
// shared-gov/StoriesSection renders. We label each ticket as a
// pseudo-epic with one column-key bucket so the breakdown bar shows
// the status colour for that single ticket. Pass an optional `people`
// array to surface the assignee's display name in the description
// (used by the General page's recent-activity feed).
export const ticketToStoryCard = (cols, t, people) => {
  const col = (cols || []).find((c) => c.key === t.column_key) || {}
  let description = t.epic_name
    ? `${t.repository || ''} · ${col.label || ''} · ${t.epic_name}`
    : `${t.repository || ''} · ${col.label || ''}`
  if (people) {
    const who = t.assignee_email
      ? people.find((p) => p.email === t.assignee_email)?.name || t.assignee_email
      : 'Unassigned'
    description = `${description} · ${who}`
  }
  return {
    id: t.id,
    title: `#${t.number || t.id} · ${t.title}`,
    name: `#${t.number || t.id} · ${t.title}`,
    description,
    color: col.color || 'GRAY',
    isTicketCard: true
  }
}

// Build a fresh newTicket payload, merging caller overrides on top of a
// clean baseline so previously-typed fields don't leak between modal opens.
// agentTest defaults to false — users opt in for genuinely test-worthy work
// (frontend changes, schema migrations with UI consumers, etc.) so the QA
// agent's queue isn't flooded by every research/copy/infra ticket. The
// AgentTestToggle UI in TicketDetailModal already renders unchecked when
// `agentTest !== true`; matching the baseline here keeps draft + UI in sync.
export function freshTicket(overrides = {}) {
  return {
    title: '',
    body: '',
    columnKey: 'backlog',
    assigneeEmail: '',
    labels: [],
    priority: 'medium',
    issueType: '',
    env: '',
    // Was missing entirely — the create-mode Epic <select> wrote to
    // `ticketDraft.epicName`, but with no baseline key here the draft's
    // value read back as `undefined` before the user touched the field
    // (harmless), and _save's create-path ticket object never carried
    // `epicName` at all (real bug, fixed alongside this). Seeded here for
    // the same reason every other free-text field is: a clean baseline so
    // `_draft(root).epicName || ''` is well-defined from the first render.
    epicName: '',
    storyPoints: '',
    acceptanceCriteria: '',
    stepsToReproduce: '',
    expectedResult: '',
    dueDate: '',
    editingTicketId: null,
    agentTest: false,
    // Dependencies picked WHILE composing a new ticket (2026-08-02 per Nika
    // "on add new ticket also show dependency field"). There is no ticket id
    // yet to write `ticket_dependencies`/`refs.dependsOn` against, so picks
    // are held here — shaped exactly like _fetchTicketDeps' `outgoing` rows
    // ({id, external_id, type, title, state, priority, assignee_email,
    // kind:'blocked_by'}) so TicketDependencyMainRow renders a pending pick
    // identically to a persisted one. _save's create branch attaches each
    // one via _addDependency once the real id exists, then clears this.
    _pendingDeps: [],
    ...overrides
  }
}

// Raw Mongo TicketComment doc → the shape TicketCommentRow renders.
//
// The comment schema (server/src/core/models/TicketComment.js) stores
// `authorEmail`, `ticket` and Mongoose `createdAt` timestamps, but the row
// reads the Supabase-era `author_email` / `created_at`. Comments posted
// in-session were built optimistically in the snake_case shape and rendered
// fine; on refresh the SAME comments came back raw from
// `sdk.tickets.comments.list` with neither key, so the author name AND the
// timestamp rendered empty (#comment-author-blank-after-refresh). Same class
// of bug _mongoToTicket exists to prevent — this is the single doc→comment
// mapper; do not hand-roll a second one.
//
// `people` is the workspace roster (`root.people`); the row prefers the
// resolved first name and falls back to the raw author email.
//
// `edited_at` is the record's own edit stamp (`editedAt`, the field the
// shared Comment and ChatMessage models carry). The row shows "edited" only
// when the record says so. It never guesses from `updatedAt`: migrations and
// backfills move `updatedAt` on comments nobody edited.
export const normalizeCommentDoc = (doc, people = []) => {
  const authorEmail = doc.authorEmail || doc.author_email || null
  return {
    ...doc,
    id: doc._id || doc.id,
    ticket_id: doc.ticket || doc.ticket_id || null,
    author_email: authorEmail,
    created_at: doc.createdAt || doc.created_at || null,
    created_at_ts:
      doc.createdAt || doc.created_at ? +new Date(doc.createdAt || doc.created_at) : null,
    edited_at: doc.editedAt || doc.edited_at || null,
    body: doc.body || '',
    _name: ((people || []).find((p) => p.email === authorEmail)?.name || authorEmail || '').split(
      ' '
    )[0]
  }
}

// Who may edit or delete a ticket comment — the SERVER's rule, mirrored.
// TicketService.updateComment / deleteComment match `authorEmail` exactly
// against the caller's email, for every role: no owner or admin may change
// another person's comment, so the UI offers no moderation either. A row
// that is not saved yet (an optimistic `optimistic-…` id, or an AI-mode row
// with no id) has nothing on the server to change.
export const canManageTicketComment = (comment, me) => {
  if (!comment || !me) return false
  const id = comment._id || comment.id
  if (!id || String(id).indexOf('optimistic-') === 0) return false
  return comment.author_email === me
}

// ── Ticket body text: the write path and the read path ─────────────────────
//
// Ticket bodies (description, acceptance criteria, steps to reproduce,
// expected result, comments) are typed in contenteditable editors and saved
// as the editor's innerHTML. A browser keeps a typed trailing space, and the
// second of two spaces, as U+00A0 so that it stays visible, and innerHTML
// writes that character as the ENTITY `&nbsp;`. A body with no formatting tag
// (`სმს იგვიანებს&nbsp;`) takes the markdown path on read, and the markdown
// renderer paints TEXT nodes, so the entity showed as six literal characters
// (aihouse, 2026-10-30: a task description and 8 of its first 10 comments).
//
// WRITE — cleanTicketEditorHtml: a body leaves the editor with plain spaces,
// never U+00A0 or its entity, and an editor that holds only blank lines is
// empty.
//
// READ — decodeTicketEntities / ticketBodyMarkdown: a stored body on the
// TEXT path gets its character references back as characters (the editor
// also writes `&amp;`, `&lt;` and `&gt;` for a typed `&`, `<` and `>`). The
// output is TEXT for the markdown renderer, which builds text nodes. Never
// hand it to `html:`: a decoded `&lt;script&gt;` is what a person typed, and
// only a text node keeps it as characters.
//
// FA204: every table lives inside its function. No `{n,m}` regex quantifier
// here (see the frank note on TicketCard's `_ticketsPreviewText`).
export const decodeTicketEntities = (text) => {
  if (typeof text !== 'string') return ''
  if (text.indexOf('&') === -1) return text
  const NAMED = {
    nbsp: ' ',
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    mdash: '—',
    ndash: '–',
    hellip: '…',
    lsquo: '‘',
    rsquo: '’',
    ldquo: '“',
    rdquo: '”',
    laquo: '«',
    raquo: '»',
    bull: '•',
    middot: '·',
    copy: '©',
    reg: '®',
    trade: '™',
    euro: '€',
    times: '×'
  }
  // ONE pass, so `&amp;lt;` reads `&lt;` (what was typed), never `<`.
  return text.replace(/&(#[0-9]+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g, (ref, name) => {
    if (name.charAt(0) === '#') {
      const hex = name.charAt(1) === 'x' || name.charAt(1) === 'X'
      const code = parseInt(name.slice(hex ? 2 : 1), hex ? 16 : 10)
      // U+00A0 reads as the plain space it stood for, as `&nbsp;` does.
      if (code === 160) return ' '
      // U+0000, the surrogates and anything past U+10FFFF are no characters.
      if (!(code > 0 && code <= 0x10ffff) || (code >= 0xd800 && code <= 0xdfff)) return ref
      return String.fromCodePoint(code)
    }
    const key = name.toLowerCase()
    return Object.prototype.hasOwnProperty.call(NAMED, key) ? NAMED[key] : ref
  })
}

// The TEXT half of a stored body, ready for the markdown renderer: the
// editor's line markup (`<div>` per line, `<br>` per break) becomes a
// paragraph break, so two typed lines never glue into one word, and every
// character reference becomes its character. Used by every read path that
// renders a body that is not rich HTML — the rich fields and the comments.
// LAST, after the decode, a link to any scheme but http, https or mailto
// loses its link (ticketSafeMarkdownLinks): `&#106;avascript:` is only
// `javascript:` once it is decoded.
export const ticketBodyMarkdown = (body) => {
  if (typeof body !== 'string' || !body) return ''
  return ticketSafeMarkdownLinks(
    decodeTicketEntities(
      body
        .replace(/<br\s*\/?>/gi, '\n\n')
        .replace(/<div(?:\s[^>]*)?>/gi, '\n\n')
        .replace(/<\/div\s*>/gi, '')
    )
      .replace(/^\n+/, '')
      .replace(/\s+$/, '')
  )
}

// The WRITE half: what an editor's innerHTML becomes before it is stored.
// U+00A0 — as the character or as any of its references — becomes a plain
// space, and an editor that holds no visible text (`<div><br></div>`, a lone
// `&nbsp;`) is empty, so a blank comment is never posted. Formatting markup
// stays exactly as the editor wrote it.
export const cleanTicketEditorHtml = (html) => {
  if (typeof html !== 'string') return ''
  const out = html.replace(/&nbsp;|&#160;|&#x0*a0;| /gi, ' ').trim()
  const visible = decodeTicketEntities(out.replace(/<[^>]*>/g, '')).trim()
  if (!visible && !/<(?:img|hr)\b/i.test(out)) return ''
  return out
}

// A stored body as plain TEXT, for a read-only panel that prints through
// `text:` (the story panel's description and acceptance criteria, which
// showed raw `<div>` and `&nbsp;`). Line markup becomes a newline, a list item
// a bullet, every other tag goes — and only THEN do the character references
// decode, so a typed `<b>` (stored as `&lt;b&gt;`) reads as those three
// characters and is never stripped as a tag. TEXT only: never `html:`.
export const ticketBodyPlainText = (body) => {
  if (typeof body !== 'string' || !body) return ''
  return decodeTicketEntities(
    body
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<li(?:\s[^>]*)?>/gi, '\n• ')
      .replace(
        /<(?:div|p|h[1-6]|blockquote)(?:\s[^>]*)?>|<\/(?:p|ul|ol|h[1-6]|blockquote)\s*>/gi,
        '\n'
      )
      .replace(/<\/?[a-zA-Z][^>]*>/g, '')
  )
    .replace(/[^\S\n]+\n/g, '\n')
    .replace(/\n\n\n+/g, '\n\n')
    .trim()
}

// ── Stored markup: one allow-list for every place a body becomes markup ────
//
// The server stores a ticket body and a comment body as the client sent it,
// so anyone who can write one can store any markup through the SDK — and it
// ran for every person who opened the ticket: an `<img onerror>` beside one
// `<b>`, a `javascript:` link, a `<style>` over the whole page. Neither the
// dev nor the prod host sends a script policy that would stop it (only
// `frame-ancestors`, 2026-10-30).
//
// So every place in this package that turns a stored body, a draft or a
// paste into markup writes `sanitizeTicketHtml(…)`, every stored URL that
// becomes an `href` passes `ticketSafeUrl`, and the markdown path keeps its
// links to the same schemes (ticketSafeMarkdownLinks, via ticketBodyMarkdown).

// A URL that may stand in an `href`: http, https, mailto, or a relative link
// (it resolves against the app's own origin) — returned as given. Any other
// scheme (`javascript:`, `data:`, `vbscript:`, `file:`…) gives ''. The scheme
// is read the way a browser's URL parser reads it: leading controls and
// spaces are skipped, and tab, LF and CR are dropped anywhere, so
// ` java\tscript:` is `javascript:` here too.
export function ticketSafeUrl(url) {
  if (typeof url !== 'string') return ''
  const bare = url.replace(/[\t\n\r]/g, '')
  let start = 0
  while (start < bare.length && bare.charCodeAt(start) <= 32) start++
  if (start === bare.length) return ''
  const scheme = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(bare.slice(start))
  if (!scheme) return url
  const name = scheme[1].toLowerCase()
  return name === 'http' || name === 'https' || name === 'mailto' ? url : ''
}

// The markdown path. The renderer (symbolize-markdown) builds TEXT nodes,
// with two exceptions that reach an `href` as written: `[label](target)` —
// the target runs from `](` to the next `)` — and `<scheme:…>`. A link whose
// target could name a scheme ticketSafeUrl refuses loses its link and keeps
// its characters:
//   `](` → `]\(` — `]` is no longer followed by `(`, whether the renderer
//     reads the `\` as an escape (a literal `(`) or not (in a code span);
//   `<scheme:` → `< scheme:` — an autolink must start with a letter.
// The target is read every way the renderer may read it: with and without
// its `\` escapes (`javascript\:` is `javascript:` to it), with and without
// a `<` around it, and as a browser reads a URL.
export function ticketSafeMarkdownLinks(text) {
  if (typeof text !== 'string' || !text) return ''
  const unescape = (s) => s.replace(/\\([!-\/:-@\[-`{-~])/g, '$1')
  const allowed = (target) => {
    const views = [target, unescape(target)]
    for (const view of views) {
      if (view && !ticketSafeUrl(view)) return false
      if (view.charAt(0) === '<' && view.length > 1 && !ticketSafeUrl(view.slice(1))) return false
    }
    return true
  }
  // What decides a target's scheme: spaces and controls (a browser skips
  // them), one leading `<`, scheme characters and `\` escapes, up to the
  // first `:`. Any other character ends a scheme, so it ends the look.
  const schemePart = (from) => {
    let j = from
    if (text.charAt(j) === '<') j++
    while (j < text.length) {
      const c = text.charAt(j)
      if (c === ':') return text.slice(from, j + 1)
      if (c === '\\' && j + 1 < text.length) {
        j += 2
        continue
      }
      if (text.charCodeAt(j) <= 32 || /[a-zA-Z0-9+.-]/.test(c)) {
        j++
        continue
      }
      break
    }
    return text.slice(from, j)
  }
  let out = ''
  let last = 0
  for (let i = text.indexOf(']('); i !== -1; i = text.indexOf('](', i + 1)) {
    if (allowed(schemePart(i + 2))) continue
    out += text.slice(last, i + 1) + '\\'
    last = i + 1
  }
  out += text.slice(last)
  return out.replace(/<([a-zA-Z][a-zA-Z0-9+.-]+:)/g, (match, scheme) =>
    ticketSafeUrl(scheme) ? match : '< ' + scheme
  )
}

// The HTML path: the one allow-list for stored body markup. The tags are what
// the ticket editors write — the toolbar (b, i, u, strike, code, a,
// blockquote, ul / ol / li, hr) and typed lines (div, br) — and what a rich
// paste brings that reads the same (p, span, strong, em, s, del, pre, h1–h6,
// tables). No attribute is kept but a link's `href`, and only one that
// ticketSafeUrl allows; a kept link opens in a new tab, as every other link
// in a ticket does (`target="_blank" rel="noopener noreferrer"`). Any other
// tag goes and its text stays (`<form>`, `<font>`, `<img>` has none); a tag
// whose content is never text a person wrote goes WITH that content (script,
// style, iframe, svg, math, template, object, select, textarea…); comments
// and doctypes go.
//
// The output is BUILT, never copied: every tag in it is one this function
// wrote, text is escaped (a character reference stays as it is, a bare `&` is
// written `&amp;`), and an `href` is the decoded value that passed the check,
// quoted again. So a browser reads the output exactly as this function did,
// however odd the input. A clean editor body comes back unchanged. Pure string
// work, no DOM: it runs before the shell has a document, in a frank-served
// runtime and in the offline tests alike.
export function sanitizeTicketHtml(html) {
  if (typeof html !== 'string' || !html) return ''
  const KEEP = {
    a: true,
    b: true,
    blockquote: true,
    br: true,
    code: true,
    del: true,
    div: true,
    em: true,
    h1: true,
    h2: true,
    h3: true,
    h4: true,
    h5: true,
    h6: true,
    hr: true,
    i: true,
    li: true,
    ol: true,
    p: true,
    pre: true,
    s: true,
    span: true,
    strike: true,
    strong: true,
    table: true,
    tbody: true,
    td: true,
    tfoot: true,
    th: true,
    thead: true,
    tr: true,
    u: true,
    ul: true
  }
  const VOID = { br: true, hr: true }
  const DROP = {
    applet: true,
    frameset: true,
    iframe: true,
    math: true,
    noembed: true,
    noframes: true,
    noscript: true,
    object: true,
    script: true,
    select: true,
    style: true,
    svg: true,
    template: true,
    textarea: true,
    title: true,
    xmp: true
  }
  const has = (map, key) => Object.prototype.hasOwnProperty.call(map, key)
  const isSpace = (c) => c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f'
  const isAlpha = (c) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z')
  const escText = (t) =>
    t
      .replace(/&(?!#[0-9]+;|#[xX][0-9a-fA-F]+;|[a-zA-Z][a-zA-Z0-9]*;)/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  const escAttr = (v) =>
    v.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  // An href as a browser reads the attribute before its URL parser sees it:
  // every numeric reference, with its `;` or without (`&#106avascript:`), the
  // named ones that spell URL syntax (`&colon;` `&Tab;` `&NewLine;` `&plus;`
  // `&period;`), and the ones decodeTicketEntities knows. One pass, so
  // `&amp;#106;` stays `&#106;`.
  const URL_NAMED = { colon: ':', Tab: '\t', NewLine: '\n', plus: '+', period: '.' }
  const decodeHref = (v) =>
    v.replace(
      /&(?:#([0-9]+);?|#[xX]([0-9a-fA-F]+);?|([a-zA-Z][a-zA-Z0-9]*);)/g,
      (ref, dec, hex, name) => {
        if (name) return has(URL_NAMED, name) ? URL_NAMED[name] : decodeTicketEntities(ref)
        const code = dec ? parseInt(dec, 10) : parseInt(hex, 16)
        if (!(code > 0 && code <= 0x10ffff) || (code >= 0xd800 && code <= 0xdfff)) return '\uFFFD'
        return String.fromCodePoint(code)
      }
    )
  const n = html.length
  // Just past the first `close` at or after `from`, or the end of the input.
  const past = (from, close) => {
    const at = html.indexOf(close, from)
    return at === -1 ? n : at + close.length
  }
  const open = []
  let out = ''
  let i = 0
  while (i < n) {
    const lt = html.indexOf('<', i)
    if (lt === -1) {
      out += escText(html.slice(i))
      break
    }
    out += escText(html.slice(i, lt))
    i = lt
    const next = html.charAt(i + 1)
    if (next === '!') {
      if (html.startsWith('<!--', i)) {
        // A browser ends `<!-->` and `<!--->` at once, any other comment at
        // the first `-->` or `--!>`.
        if (html.charAt(i + 4) === '>') i += 5
        else if (html.startsWith('->', i + 4)) i += 6
        else {
          const dashes = html.indexOf('-->', i + 4)
          const bang = html.indexOf('--!>', i + 4)
          if (dashes === -1 && bang === -1) i = n
          else if (dashes === -1 || (bang !== -1 && bang < dashes)) i = bang + 4
          else i = dashes + 3
        }
        continue
      }
      i = past(i + 2, '>')
      continue
    }
    if (next === '?') {
      i = past(i + 2, '>')
      continue
    }
    const closing = next === '/'
    const nameAt = closing ? i + 2 : i + 1
    if (!isAlpha(html.charAt(nameAt))) {
      if (closing) {
        // `</>` is nothing; `</` and no letter is a comment to the next `>`.
        i = html.charAt(nameAt) === '>' ? nameAt + 1 : past(nameAt, '>')
        continue
      }
      // A `<` that opens no tag is text.
      out += '&lt;'
      i += 1
      continue
    }
    let k = nameAt
    while (k < n && !isSpace(html.charAt(k)) && html.charAt(k) !== '/' && html.charAt(k) !== '>')
      k++
    const name = html.slice(nameAt, k).toLowerCase()
    // The attributes, read as a browser reads them so the tag ends where its
    // `>` really is; only `href` is kept for later.
    let href = null
    let selfClosing = false
    let ended = false
    while (k < n) {
      const c = html.charAt(k)
      if (isSpace(c)) {
        k++
        continue
      }
      if (c === '/') {
        if (html.charAt(k + 1) === '>') {
          selfClosing = true
          ended = true
          k += 2
          break
        }
        k++
        continue
      }
      if (c === '>') {
        ended = true
        k++
        break
      }
      let e = k + 1
      while (e < n && !isSpace(html.charAt(e)) && '/>='.indexOf(html.charAt(e)) === -1) e++
      const attrName = html.slice(k, e).toLowerCase()
      k = e
      while (k < n && isSpace(html.charAt(k))) k++
      let value = ''
      if (html.charAt(k) === '=') {
        k++
        while (k < n && isSpace(html.charAt(k))) k++
        const quote = html.charAt(k)
        if (quote === '"' || quote === "'") {
          const end = html.indexOf(quote, k + 1)
          if (end === -1) {
            k = n
            break
          }
          value = html.slice(k + 1, end)
          k = end + 1
        } else {
          let end = k
          while (end < n && !isSpace(html.charAt(end)) && html.charAt(end) !== '>') end++
          value = html.slice(k, end)
          k = end
        }
      }
      if (attrName === 'href' && href === null) href = value
    }
    // A tag the input ends inside is no tag: a browser drops it and the rest.
    if (!ended) break
    i = k
    if (closing) {
      if (!has(KEEP, name) || has(VOID, name)) continue
      let at = open.length - 1
      while (at >= 0 && open[at].name !== name) at--
      if (at < 0) continue
      while (open.length > at) {
        const top = open.pop()
        if (top.shown) out += '</' + top.name + '>'
      }
      continue
    }
    if (name === 'plaintext') break
    if (has(DROP, name)) {
      // svg and math honour `/>`, so there is nothing inside to drop.
      if (selfClosing && (name === 'svg' || name === 'math')) continue
      const endTag = new RegExp('</' + name + '(?=[\\t\\n\\f\\r />])', 'gi')
      endTag.lastIndex = i
      const found = endTag.exec(html)
      i = found ? past(found.index + 2, '>') : n
      continue
    }
    if (!has(KEEP, name)) continue
    if (has(VOID, name)) {
      out += '<' + name + '>'
      continue
    }
    if (name === 'a') {
      // A link to a refused scheme keeps its words and loses its link.
      const url = ticketSafeUrl(decodeHref(href || ''))
      open.push({ name, shown: !!url })
      if (url) out += '<a href="' + escAttr(url) + '" target="_blank" rel="noopener noreferrer">'
      continue
    }
    open.push({ name, shown: true })
    out += '<' + name + '>'
  }
  while (open.length) {
    const top = open.pop()
    if (top.shown) out += '</' + top.name + '>'
  }
  return out
}
